import React, { useRef, useState } from "react";
import { useNavigate, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Loader2, ArrowRight } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { MfaChallenge, MfaSetup, BackupCodes } from "../components/MfaSteps";
import { Turnstile, useTurnstileEnforced } from "../components/Turnstile";

export default function LoginPage() {
  const { user, login, completeSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState(location.state?.email || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [cfToken, setCfToken] = useState("");
  const turnstileRef = useRef(null);
  const turnstileEnforced = useTurnstileEnforced();
  const [mfa, setMfa] = useState(null); // {mode: "challenge"|"setup"|"codes", token, session}

  if (user && user !== false && !mfa) return <Navigate to="/" replace />;
  const finish = (data) => { completeSession(data); setMfa(null); navigate("/"); };
  const reset = () => { setMfa(null); setPassword(""); };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const data = await login(email, password, cfToken);
      if (data.mfa_required) setMfa({ mode: "challenge", token: data.mfa_token });
      else if (data.mfa_setup_required) setMfa({ mode: "setup", token: data.mfa_token });
      else navigate("/");
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
      turnstileRef.current?.reset();
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* Left - form */}
      <div className="flex flex-col p-8 lg:p-16">
        <div className="flex items-center gap-3 mb-16">
          <img src="/brand/housing-intelligence-logo.png" alt="Housing Intelligence" className="h-12 w-12 object-contain" />
          <div className="leading-tight">
            <div className="font-heading font-black text-lg" style={{ color: "var(--hi-olive)" }}>Housing Intelligence</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">EPC Register monitoring</div>
          </div>
        </div>

        <div className="flex-1 flex flex-col justify-center max-w-md">
          <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-3">SECURE STAFF LOGIN</div>
          <h1 className="font-heading font-black text-4xl lg:text-5xl tracking-tight mb-3" style={{ color: "var(--hi-olive)" }}>
            Monitor every<br />
            <span style={{ color: "var(--hi-orange)" }}>EPC change.</span>
          </h1>
          <p className="text-muted-foreground mb-10 leading-relaxed">
            Track previous and current Energy Performance Certificates across your housing stock. Get alerted the moment a band changes on the government register.
          </p>

          {mfa?.mode === "challenge" && <MfaChallenge mfaToken={mfa.token} onSuccess={finish} onCancel={reset} />}
          {mfa?.mode === "setup" && (
            <MfaSetup intro="Your account needs two-step verification. Set it up now to continue."
              start={async () => (await api.post("/auth/mfa/setup/start", { mfa_token: mfa.token })).data}
              confirm={async (code) => (await api.post("/auth/mfa/setup/confirm", { mfa_token: mfa.token, code })).data}
              onEnrolled={(d) => setMfa({ mode: "codes", session: d })} onCancel={reset} />
          )}
          {mfa?.mode === "codes" && <BackupCodes codes={mfa.session.backup_codes} onDone={() => finish(mfa.session)} />}
          {!mfa && <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email"
                className="rounded-none h-11 border-slate-300 focus-visible:ring-slate-900"
                placeholder="you@housing.org"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password"
                className="rounded-none h-11 border-slate-300 focus-visible:ring-slate-900"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <Alert variant="destructive" className="rounded-none" data-testid="login-error">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Turnstile ref={turnstileRef} action="login" onToken={setCfToken} testId="login-turnstile" />

            <Button
              type="submit"
              disabled={loading || (turnstileEnforced && !cfToken)}
              data-testid="login-submit"
              className="w-full h-11 rounded-none text-white font-semibold group"
              style={{ backgroundColor: "var(--hi-olive)" }}
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>Sign in <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" /></>
              )}
            </Button>
          </form>}
        </div>

        <div className="mt-8 text-xs text-muted-foreground">© {new Date().getFullYear()} Housing Intelligence · A tool for UK social housing providers</div>
      </div>

      {/* Right - image */}
      <div className="hidden lg:block relative">
        <img
          src="https://images.unsplash.com/photo-1591955664169-4e2c269adecc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
          alt="UK housing"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(42,58,20,0.92) 0%, rgba(42,58,20,0.45) 50%, transparent 100%)" }} />
        <div className="absolute bottom-0 left-0 right-0 p-12 text-white">
          <div className="text-xs uppercase tracking-[0.25em] text-white/80 mb-4">UK ENERGY PERFORMANCE</div>
          <div className="font-heading font-black text-3xl leading-tight max-w-md">
            A government register, your housing stock — finally on the same page.
          </div>
          <div className="mt-6 grid grid-cols-7 gap-1 max-w-md">
            {["A","B","C","D","E","F","G"].map((b) => (
              <div key={b} className={`epc-${b.toLowerCase()} h-10 flex items-center justify-center font-heading font-black`}>{b}</div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
